import React from 'react'
import { Spacer, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import LikeHeartIcon from 'assets/icons/likeHeart'
import MoreIcon from 'assets/icons/more'
import RichText from 'src/components/utils/RichText'
import { useUpdateMessageReplyLiked } from 'src/graphql/hooks'

// =======================================================
// Component
// =======================================================

const ReplyListItem = ({
   id,
   anon,
   authorAvatarUrl,
   authorId,
   authorName,
   body,
   currentUserCanModerate,
   edited,
   dateAdded,
   isLikedByMe,
   isMine,
   messageId,
   numLikes,
   onOptionsButtonClick
}) => {
   const { updateMessageReplyLiked } = useUpdateMessageReplyLiked()
   const avatarUrl = authorAvatarUrl || '/assets/images/avatarPlaceholder.svg'

   const handleReplyLikeToggled = async (liked) => {
      await updateMessageReplyLiked({ replyId: id, messageId, liked }, {
         optimisticResponse: {
            updateMessageReplyLiked: {
               __typename: 'MessageReply',
               id,
               isLikedByMe: liked,
               numLikes: liked
                  ? numLikes + 1
                  : numLikes - 1
            }
         }
      })
   }

   return (
      <View
         float="left"
         width="100%"
         marginTop="15px"
         padding="15px 0px 0px 0px"
         borderTop="1px solid #e5e5e5">
         <View display="flex">
            <View
               flex="0 0 auto"
               float="left"
               width="30px"
               height="30px"
               borderRadius="50%"
               backgroundColor="#ddd"
               backgroundImage={`url(${avatarUrl})`}
               backgroundSize={authorAvatarUrl ? 'cover' : '21px 21px'}
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               marginRight="20px"
            />
            <View flex="1 1 auto" maxWidth="calc(100% - 50px)">
               <View display="flex" justifyContent="space-between">
                  <View
                     display="block[a-b] flex[c-f]"
                     alignItems="center">
                     <View paddingRight="6px">
                        {!anon && (
                           <Link to={`/profile/${authorId}`}>
                              <Text size="1.5[a-d] 2[e-f]" color="#222" weight="medium">
                                 {anon ? 'Anonymous' : authorName}
                              </Text>
                           </Link>
                        )}
                        {anon && (
                           <Text size="1.5[a-d] 2[e-f]" color="#222" weight="medium">
                              Anonymous
                           </Text>
                        )}
                     </View>
                     <Text size="1" color="#888" uppercase>
                        {dateAdded}
                        {edited && (
                           <View display="inline-block" marginLeft="6px">
                              <Text size="1" color="#aaa">[edited]</Text>
                           </View>
                        )}
                     </Text>
                  </View>
                  {/* TODO: Other people with message edit or remove privileges
                     should be able to also edit or remove the reply */}
                  {(isMine || currentUserCanModerate) && (
                     <View
                        position="relative"
                        top="-3px"
                        height="18px"
                        onClick={onOptionsButtonClick}>
                        <MoreIcon
                           width="24px"
                           height="24px"
                           color="#aaa"
                        />
                     </View>
                  )}
               </View>
               <View float="left" width="100%">
                  <Spacer size="1" />
                  <Text size="2[a-d] 2.5[e-f]" lineHeight="140%" color="#555">
                     <RichText html={body} />
                  </Text>
               </View>
               {numLikes > 0 && (
                  <View float="left" width="100%" marginTop="6px">
                     <Text
                        size="1"
                        lineHeight="150%"
                        color="#aaa">
                        {`${pluralize('people', numLikes, true)} like${numLikes === 1 ? 's' : ''} this`}
                     </Text>
                  </View>
               )}
               <View
                  float="left"
                  width="100%"
                  onClick={() => handleReplyLikeToggled(!isLikedByMe)}
                  style={{ cursor: 'pointer' }}>
                  <Spacer size="1" />
                  <View
                     display="inline-flex"
                     alignItems="center"
                     marginRight="18px"
                     style={{ cursor: 'pointer' }}>
                     {isLikedByMe && (
                        <View display="inline-block" paddingRight="6px">
                           <LikeHeartIcon width="12px" height="12px" color="#ff5b5b" />
                        </View>
                     )}
                     <Text
                        size="0.8"
                        weight="bold"
                        color={isLikedByMe ? '#ff5b5b' : '#888'}
                        transform="uppercase">
                        {isLikedByMe ? 'Liked' : 'Like'}
                     </Text>
                  </View>
               </View>
            </View>
         </View>
      </View>
   )
}

ReplyListItem.propTypes = {
   id: PropTypes.string.isRequired,
   anon: PropTypes.bool.isRequired,
   authorAvatarUrl: PropTypes.string,
   authorId: PropTypes.string,
   authorName: PropTypes.string,
   body: PropTypes.string.isRequired,
   currentUserCanModerate: PropTypes.bool.isRequired,
   edited: PropTypes.bool.isRequired,
   dateAdded: PropTypes.string.isRequired,
   isLikedByMe: PropTypes.bool.isRequired,
   isMine: PropTypes.bool.isRequired,
   messageId: PropTypes.string.isRequired,
   numLikes: PropTypes.number.isRequired,
   onOptionsButtonClick: PropTypes.func.isRequired
}

ReplyListItem.defaultProps = {
   authorAvatarUrl: undefined,
   authorId: undefined,
   authorName: undefined
}

export default ReplyListItem
